// src/careJobs/hooks/useJobActions.tsx
import * as React from "react";
import { gql, useMutation, useQuery, makeVar, useReactiveVar, useApolloClient } from "@apollo/client";
import JobApplyDialog from "../components/jobApplyDialog";
import type { JobCardJob } from "../components/jobCard";

/* ===== GQL ===== */
const SAVE_JOB = gql`
  mutation SaveJob($jobId: ID!) {
    saveJob(jobId: $jobId) { success message }
  }
`;

const UNSAVE_JOB = gql`
  mutation UnsaveJob($jobId: ID!) {
    unsaveJob(jobId: $jobId) { success message }
  }
`;

const APPLY_TO_JOB = gql`
  mutation ApplyToJob($input: ApplyToJobInput!) {
    applyToJob(input: $input) {
      success
      message
      application { id }
    }
  }
`;

/** Minimal query to know what's already applied */
const MY_APPS_MIN = gql`
  query MyJobAppsMin {
    myJobApplications {
      id
      jobId
      job { id }
    }
  }
`;

/* ===== Reactive var to broadcast applied jobs instantly across cards ===== */
const appliedJobIdsVar = makeVar<Set<string>>(new Set());

/* ===== Types & Options ===== */
type MinimalJob = Pick<JobCardJob, "id" | "title">;

type UseJobActionsOptions = {
  /** Refetch a small saved-jobs query after save/unsave */
  afterSavedChange?: () => Promise<any> | void;
  /** Optional hook to run after a successful apply (e.g., refetch lists) */
  afterAppliedChange?: () => Promise<any> | void;
};

export function useJobActions(opts: UseJobActionsOptions = {}) {
  const { afterSavedChange, afterAppliedChange } = opts;
  const client = useApolloClient();

  /* ---------- Save / Unsave ---------- */
  const [mutSave] = useMutation(SAVE_JOB);
  const [mutUnsave] = useMutation(UNSAVE_JOB);

  const toggleSave = React.useCallback(
    async (job: MinimalJob, nextSaved?: boolean) => {
      const jobId = job?.id != null ? String(job.id) : "";
      if (!jobId) return;

      try {
        if (nextSaved === true) {
          await mutSave({ variables: { jobId } });
        } else if (nextSaved === false) {
          await mutUnsave({ variables: { jobId } });
        } else {
          try {
            await mutSave({ variables: { jobId } });
          } catch {
            await mutUnsave({ variables: { jobId } });
          }
        }
        await afterSavedChange?.();
      } catch {
        /* surface with a toast/snackbar if desired */
      }
    },
    [mutSave, mutUnsave, afterSavedChange]
  );

  /* ---------- Applied state (query + reactive var) ---------- */
  const appsQ = useQuery(MY_APPS_MIN, { fetchPolicy: "cache-first" });
  const reactiveApplied = useReactiveVar(appliedJobIdsVar);

  // Seed reactive set from query (runs once cache has data)
  React.useEffect(() => {
    const list: any[] = appsQ.data?.myJobApplications ?? [];
    if (list.length === 0) return;
    const next = new Set<string>(reactiveApplied);
    for (const a of list) {
      const idStr =
        a?.job?.id != null ? String(a.job.id) :
        a?.jobId != null ? String(a.jobId) : "";
      if (idStr) next.add(idStr);
    }
    if (next.size !== reactiveApplied.size) {
      appliedJobIdsVar(next);
    }
  }, [appsQ.data]); // eslint-disable-line react-hooks/exhaustive-deps

  const appliedKnown = !appsQ.loading || reactiveApplied.size > 0;

  const isApplied = React.useCallback((job: MinimalJob) => {
    const idStr = job?.id != null ? String(job.id) : "";
    if (!idStr) return false;
    if (appliedJobIdsVar().has(idStr)) return true;

    // Fallback to current query snapshot
    const list: any[] = appsQ.data?.myJobApplications ?? [];
    return list.some(
      (a) => String(a?.job?.id ?? a?.jobId ?? "") === idStr
    );
  }, [appsQ.data]);

  // Helper to mark a job as applied (instant UI)
  const markApplied = React.useCallback((jobId: number | string) => {
    const idStr = String(jobId);
    const next = new Set(appliedJobIdsVar());
    next.add(idStr);
    appliedJobIdsVar(next);

    // Best-effort cache patch so future reads see it too
    try {
      const existing = client.readQuery<{ myJobApplications: any[] }>({ query: MY_APPS_MIN });
      const list = existing?.myJobApplications ?? [];
      const already = list.some((a) => String(a?.job?.id ?? a?.jobId ?? "") === idStr);
      if (!already) {
        client.writeQuery({
          query: MY_APPS_MIN,
          data: {
            myJobApplications: [
              ...list,
              {
                __typename: "JobApplication",
                id: `local-applied-${idStr}`, // temporary id
                jobId: Number.isFinite(Number(idStr)) ? Number(idStr) : null,
                job: { __typename: "JobPosting", id: idStr },
              },
            ],
          },
        });
      }
    } catch {
      /* cache may not exist yet; safe to ignore */
    }
  }, [client]);

  /* ---------- Apply dialog state ---------- */
  type ApplyState = { id: number; title: string } | null;
  const [applyOpen, setApplyOpen] = React.useState(false);
  const [applyJob, setApplyJob] = React.useState<ApplyState>(null);

  const openApply = React.useCallback((j: MinimalJob) => {
    const numId = Number(j?.id);
    if (!Number.isFinite(numId)) return;
    setApplyJob({ id: numId, title: (j?.title ?? "").toString() });
    setApplyOpen(true);
  }, []);

  const closeApply = React.useCallback(() => {
    setApplyOpen(false);
    setApplyJob(null);
  }, []);

  // Optional: expose the raw mutation if you need programmatic submit somewhere else
  const [mutApply] = useMutation(APPLY_TO_JOB);
  const applyToJob = React.useCallback(
    async (input: {
      jobId: number | string;
      applicantName: string;
      applicantEmail?: string | null;
      applicantPhone?: string | null;
      coverLetter?: string | null;
      resumeUrl?: string | null;
      createAccount?: boolean | null;
    }) => {
      return mutApply({ variables: { input } });
    },
    [mutApply]
  );

  // Renderable dialog (mount once). We inject onApplied → markApplied → optional afterAppliedChange
  const ApplyDialog = React.useMemo(
    () => (
      <JobApplyDialog
        open={applyOpen}
        onClose={closeApply}
        jobId={applyJob?.id ?? null}
        jobTitle={applyJob?.title}
        onApplied={(jid) => {
          if (jid != null) {
            markApplied(jid);
          }
          Promise.resolve(afterAppliedChange?.()).catch(() => {});
        }}
      />
    ),
    [applyOpen, closeApply, applyJob, markApplied, afterAppliedChange]
  );

  // Optional share helper
  const share = React.useCallback((job: MinimalJob) => {
    try {
      const base = new URL(window.location.href);
      if (job?.id != null) base.searchParams.set("job", String(job.id));
      const url = base.toString();
      const title = (job?.title && String(job.title).trim()) || "Job";
      if ((navigator as any).share) {
        (navigator as any).share({ title, text: title, url }).catch(() => {});
      } else if (navigator.clipboard) {
        navigator.clipboard.writeText(url).catch(() => {});
      } else {
        window.prompt("Copy this link:", url);
      }
    } catch { /* no-op */ }
  }, []);

  return {
    // saving
    toggleSave,

    // applying
    openApply,
    closeApply,
    ApplyDialog,
    applyToJob,

    // applied state for consumers (e.g., JobCard)
    isApplied,
    appliedKnown,

    // share
    share,
  };
}

export default useJobActions;
